import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  FaBaby,
  FaCalendarAlt,
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
  FaEnvelope,
  FaHourglassHalf,
  FaInfoCircle,
  FaPhone,
  FaRegCopy,
  FaSearch,
  FaTimes,
  FaTrash,
  FaUndo,
  FaUniversity,
  FaUser,
  FaUserEdit,
  FaUsers,
} from "react-icons/fa";
import { parseDateArray } from "../utils/dateUtils";
import http from "../api/apiClient";
import type {
  ChildSummary,
  DecoratedMutationRequest,
  EntityId,
  FamilySummary,
  LookupMap,
  ParentSummary,
  UserSummary,
} from "../pages/Administration/Management/MutationRequests/types/mutationRequests.types";

const statusColors: Record<string, string> = {
  APPROVED: "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100",
  REJECTED: "bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100",
  PENDING: "bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-100",
};

const REASON_LABELS: Record<string, string> = {
  DIVORCE: "Scheiding",
  NEW_PARTNER: "Nieuwe partner",
  SINGLE_PARENT: "Alleenstaande ouder",
  CHILD_BELONGS_TO_OTHER_FAMILY: "Kind hoort bij ander gezin",
  OTHER: "Anders",
};

const FIELD_LABELS: Record<string, string> = {
  accountHolderName: "Rekeninghouder",
  bsn: "BSN",
  childId: "Kind",
  contactRequested: "Contact gewenst",
  dateBirth: "Geboortedatum",
  dateRegistration: "Registratiedatum",
  familySituation: "Gezinssituatie",
  explanation: "Toelichting",
  familyId: "Familie",
  familyName: "Familie",
  familySituationChange: "Gezinssituatie",
  firstName: "Voornaam",
  gender: "Geslacht",
  iban: "IBAN",
  lastName: "Achternaam",
  note: "Toelichting",
  parentId: "Ouder",
  parentUuid: "Ouder",
  proposedAccountHolderName: "Rekeninghouder",
  proposedIban: "IBAN",
  reason: "Gezinssituatie",
  wantsContact: "Contact gewenst",
};

const SENSITIVE_FIELDS = new Set(["bsn", "socialsecuritynumber"]);

function maskSensitiveValue(field, value) {
  if (!SENSITIVE_FIELDS.has(String(field).toLowerCase())) {
    return value;
  }

  if (value == null || value === "") return "••••";

  const digits = String(value).replace(/\D/g, "");

  if (!digits) return "••••";

  return `••••${digits.slice(-3)}`;
}

const maskSensitiveData = (value, field = "") => {
  if (Array.isArray(value)) {
    return value.map((item) => maskSensitiveData(item, field));
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, maskSensitiveData(item, key)])
    );
  }

  return maskSensitiveValue(field, value);
};

const maskSensitiveText = (value) =>
  String(value ?? "").replace(
    /("(?:bsn|socialSecurityNumber)"\s*:\s*")([^"]*)(")/gi,
    (_match, prefix, rawValue, suffix) => `${prefix}${maskSensitiveValue("bsn", rawValue)}${suffix}`
  );

const parseMutationTimestamp = (value) => {
  if (value == null) return null;
  if (Array.isArray(value)) return parseDateArray(value);
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") {
    const date = new Date(value < 10_000_000_000 ? value * 1000 : value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const localDateTime = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?$/
  );
  if (localDateTime) {
    const [, year, month, day, hour, minute, second = "0", fraction = "0"] = localDateTime;
    const millisecond = Number(fraction.padEnd(3, "0").slice(0, 3));
    const date = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
      millisecond
    );
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const dateOnly = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (value) => {
  const date = parseMutationTimestamp(value);
  return !date
    ? "—"
    : date.toLocaleString("nl-NL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
};

const formatDateOnly = (value) => {
  const date = parseMutationTimestamp(value);
  return !date
    ? String(value ?? "—")
    : date.toLocaleDateString("nl-NL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
};

const formatRelativeTime = (value) => {
  const date = parseMutationTimestamp(value);
  if (!date) return "—";
  const diffSeconds = Math.round((Date.now() - date.getTime()) / 1000);
  const past = diffSeconds >= 0;
  const absSeconds = Math.abs(diffSeconds);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["year", 31536000],
    ["month", 2592000],
    ["week", 604800],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  const formatter = new Intl.RelativeTimeFormat("nl-NL", { numeric: "auto" });
  for (const [unit, seconds] of units) {
    if (absSeconds >= seconds) return formatter.format((past ? -1 : 1) * Math.round(absSeconds / seconds), unit);
  }
  return formatter.format(0, "second");
};

const parsePayload = (payload) => {
  if (!payload) return {};
  if (typeof payload === "object") return payload;
  try {
    return JSON.parse(payload);
  } catch {
    return {};
  }
};

const formatRawPayload = (payload) => {
  try {
    const parsed = typeof payload === "string" ? JSON.parse(payload) : payload;
    return JSON.stringify(maskSensitiveData(parsed), null, 2);
  } catch {
    return maskSensitiveText(payload);
  }
};

const lookupEntryToLabel = (entry, fallback = "") => {
  if (entry == null) return fallback;
  if (typeof entry === "string" || typeof entry === "number" || typeof entry === "boolean") return String(entry);
  if (typeof entry === "object") {
    return (
      entry.displayName ||
      entry.display_name ||
      entry.fullName ||
      entry.fullname ||
      entry.name ||
      entry.email ||
      entry.id ||
      fallback
    );
  }
  return fallback;
};

const resolveLabel = (identifier, lookup) => {
  if (identifier == null) return "—";
  const normalized = String(identifier).trim();
  if (!normalized) return "—";
  if (lookup && typeof lookup.get === "function") {
    return lookupEntryToLabel(lookup.get(normalized) || lookup.get(normalized.toLowerCase()), normalized);
  }
  return lookupEntryToLabel(lookup?.[normalized] || lookup?.[normalized.toLowerCase()], normalized);
};

const getLookupEntry = (identifier, lookup) => {
  if (identifier == null || !lookup) return null;
  const normalized = String(identifier).trim();
  if (!normalized) return null;
  if (typeof lookup.get === "function") {
    return lookup.get(normalized) || lookup.get(normalized.toLowerCase()) || null;
  }
  return lookup[normalized] || lookup[normalized.toLowerCase()] || null;
};

const getPersonName = (value) => {
  if (!value || typeof value !== "object") return null;
  return (
    [value.firstName, value.lastName].filter(Boolean).join(" ").trim() ||
    [value.first_name, value.last_name].filter(Boolean).join(" ").trim() ||
    value.displayName ||
    value.display_name ||
    value.fullName ||
    value.fullname ||
    value.name ||
    value.email ||
    null
  );
};

const labelForField = (key) =>
  FIELD_LABELS[key] ||
  String(key || "")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (char) => char.toUpperCase());

const getLookupForField = (field, lookups) => {
  if (field === "childId") return lookups.childLookup;
  if (field === "familyId") return lookups.familyLookup;
  if (field === "parentUuid" || field === "parentId") return lookups.parentLookup || lookups.userLookup;
  return null;
};

const toDisplayValue = (value, lookup = null) => {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Ja" : "Nee";
  if (Array.isArray(value)) return value.length ? value.map((item) => toDisplayValue(item)).join(", ") : "—";
  if (typeof value === "object") {
    return getPersonName(value) || value.familyName || value.name || value.displayName || value.id || JSON.stringify(value);
  }
  const str = String(value);
  if (lookup && typeof lookup.get === "function") {
    return lookupEntryToLabel(lookup.get(str) || lookup.get(str.toLowerCase()), str);
  }
  return str;
};

const getFieldValue = (key, value, lookups) => {
  const maskedValue = maskSensitiveValue(key, value);
  if (key === "reason" && typeof value === "string") return REASON_LABELS[value] || value;
  const normalizedKey = String(key || "").replace(/[\s_-]+/g, "").toLowerCase();
  if (["datebirth", "birthdate", "dateofbirth", "geboortedatum"].includes(normalizedKey)) {
    return formatDateOnly(value);
  }
  return toDisplayValue(maskedValue, getLookupForField(key, lookups));
};

const valuesEqual = (a, b) => {
  if ((a == null || a === "") && (b == null || b === "")) return true;
  return String(a) === String(b);
};

const buildFallbackView = (req, parentLookup, userLookup) => ({
  id: req.id,
  title: `${req.entityType || "Mutatie"}${req.action ? ` (${req.action})` : ""}`,
  subtitle: "",
  entityType: req.entityType,
  action: req.action,
  status: req.status,
  requestedByName: resolveLabel(req.requestedBy, parentLookup || userLookup),
  requestedByUserId: req.requestedBy,
  createdAt: req.createdAt,
  sections: [],
  rawPayload: req.payload,
});

const getChangedMutationFields = (req, view, lookups = {}) => {
  const sections = Array.isArray(view?.sections) ? view.sections : [];
  const sectionRows = sections
    .flatMap((section) => section.fields || [])
    .filter((field) => field && (field.oldValue != null || field.newValue != null || field.value != null))
    .map((field) => {
      const key = field.key || field.name || field.field;
      const sensitiveKey = key || field.label;
      return {
        key,
        label: field.label || labelForField(key),
        oldValue: getFieldValue(sensitiveKey, field.oldValue, lookups),
        newValue: getFieldValue(sensitiveKey, field.newValue ?? field.value, lookups),
        value: getFieldValue(sensitiveKey, field.value ?? field.newValue, lookups),
      };
    })
    .filter((row) => row.newValue !== "—" && !valuesEqual(row.oldValue, row.newValue));

  if (sectionRows.length) return sectionRows;

  const payload = parsePayload(view?.rawPayload ?? req.payload);
  const ignored = new Set(["id", "entityId", "childId", "parentUuid", "parentId"]);
  return Object.entries(payload)
    .filter(([key, value]) => !ignored.has(key) && value != null && value !== "")
    .map(([key, value]) => ({
      key,
      label: labelForField(key),
      oldValue: "—",
      newValue: getFieldValue(key, value, lookups),
      value: getFieldValue(key, value, lookups),
    }));
};

const findRow = (rows, keysOrLabels) => {
  const wanted = new Set(keysOrLabels.map((key) => String(key).toLowerCase()));
  return rows.find((row) => wanted.has(String(row.key || row.label).toLowerCase()));
};

const getExplanation = (rows) => {
  const row = findRow(rows, ["explanation", "note", "Toelichting"]);
  return row?.newValue || row?.value || "";
};

const getContactRequested = (rows) => {
  const row = findRow(rows, ["wantsContact", "contactRequested", "Contact gewenst"]);
  return ["ja", "true", "yes", "1"].includes(String(row?.newValue ?? row?.value ?? "").trim().toLowerCase());
};

const getFamilySituationChange = (rows) => findRow(rows, ["reason", "familySituationChange", "Gezinssituatie"]);

const isFamilySituationRequest = (req, view, rows) => {
  const payload = parsePayload(view?.rawPayload ?? req.payload);
  return Boolean(payload.reason || payload.familySituationChange || payload.wantsContact || payload.contactRequested || getFamilySituationChange(rows));
};

const getMutationTitle = (req, view) => {
  const payload = parsePayload(view?.rawPayload ?? req.payload);
  const entity = String(req.entityType || view?.entityType || "").toUpperCase();
  const action = String(req.action || view?.action || "").toUpperCase();

  if (payload.reason || payload.familySituationChange || payload.wantsContact || payload.contactRequested) return "Gezinssituatie wijzigen";
  if (entity === "CHILD" && action === "CREATE") return "Kind toevoegen";
  if (entity === "CHILD" && action === "UPDATE") return "Kind wijzigen";
  if ((entity === "FAMILY" || entity === "PARENT") && action === "UPDATE") return "Ouder wijzigen";
  if (entity === "BANK_ACCOUNT") return "Bankrekening wijzigen";
  return view?.title || `${entity || "Mutatie"}${action ? ` (${action})` : ""}`;
};

const getMutationIcon = (req, view) => {
  const payload = parsePayload(view?.rawPayload ?? req.payload);
  const entity = String(req.entityType || view?.entityType || "").toUpperCase();
  if (payload.reason || payload.familySituationChange || payload.wantsContact || payload.contactRequested) return FaUserEdit;
  if (entity === "CHILD") return FaBaby;
  if (entity === "BANK_ACCOUNT") return FaUniversity;
  return FaUserEdit;
};

const getSubjectName = (req, view, lookups) => {
  const payload = parsePayload(view?.rawPayload ?? req.payload);
  const source = payload.child || payload.family || payload.entity || payload;
  const entity = String(req.entityType || view?.entityType || "").toUpperCase();

  if (view?.subtitle) return view.subtitle;
  if (source.familyName) return source.familyName;
  if (source.family) return toDisplayValue(source.family);
  if (entity === "CHILD") return getPersonName(source) || resolveLabel(source.childId || req.entityId, lookups.childLookup);
  if (entity === "FAMILY") return resolveLabel(source.familyId || req.entityId, lookups.familyLookup);
  if (entity === "BANK_ACCOUNT") return resolveLabel(source.parentUuid || source.parentId || req.entityId, lookups.parentLookup || lookups.userLookup);
  return getPersonName(source) || (req.entityId ? `#${req.entityId}` : "—");
};

const pickFirst = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    const trimmed = String(value).trim();
    if (trimmed && trimmed !== "—") return trimmed;
  }
  return "";
};

const pickObject = (...values) => {
  for (const value of values) {
    if (value && typeof value === "object" && !Array.isArray(value)) return value;
  }
  return null;
};

const getRequestContext = (req, view, lookups) => {
  const payload = parsePayload(view?.rawPayload ?? req.payload);
  const parentLookupEntry = getLookupEntry(req.requestedBy, lookups.parentLookup);
  const parent =
    payload.parent ||
    payload.parentData ||
    payload.requestedBy ||
    parentLookupEntry?.raw ||
    parentLookupEntry ||
    {};
  const requestedById = view?.requestedByUserId || req.requestedBy;
  const requestedBy = pickFirst(view?.requestedByName, getPersonName(parent), resolveLabel(requestedById, lookups.parentLookup || lookups.userLookup));
  const email = pickFirst(view?.requestedByEmail, parent.email, parent.mail, payload.email, payload.parentEmail);
  const phone = pickFirst(view?.requestedByPhone, parent.phone, parent.mobile, parent.telephone, payload.phone, payload.parentPhone);
  const familyName = pickFirst(payload.familyName, payload.family?.familyName, payload.family?.name, getSubjectName(req, view, lookups));

  return {
    requestedBy,
    requestedById,
    email,
    phone,
    familyName,
    createdAt: formatDateTime(view?.createdAt || req.createdAt),
    relativeCreatedAt: formatRelativeTime(view?.createdAt || req.createdAt),
    status: String(req.status || view?.status || "PENDING").toUpperCase(),
  };
};

const getParentDetails = (req, view, lookups, context) => {
  const payload = parsePayload(view?.rawPayload ?? req.payload);
  const requestedById = context?.requestedById || view?.requestedByUserId || req.requestedBy;
  const lookupEntry = getLookupEntry(requestedById, lookups.parentLookup);
  const raw = pickObject(
    lookupEntry?.raw,
    lookupEntry,
    payload.parent,
    payload.parentData,
    typeof payload.requestedBy === "object" ? payload.requestedBy : null
  );
  const id = pickFirst(
    raw?.id,
    raw?.uuid,
    raw?.parentId,
    raw?.parentUuid,
    lookupEntry?.id,
    requestedById
  );

  return {
    id,
    raw,
    name: pickFirst(context?.requestedBy, getPersonName(raw), lookupEntry?.displayName),
    email: pickFirst(context?.email, raw?.email, raw?.emailAddress, lookupEntry?.email),
    phone: pickFirst(context?.phone, raw?.phone, raw?.phoneNumber, raw?.mobile, raw?.mobilePhone, lookupEntry?.phone),
  };
};

const parentFieldLabel = (key) =>
  ({
    id: "ID",
    uuid: "UUID",
    firstName: "Voornaam",
    lastName: "Achternaam",
    email: "E-mail",
    emailAddress: "E-mail",
    phone: "Telefoon",
    phoneNumber: "Telefoon",
    mobile: "Mobiel",
    mobilePhone: "Mobiel",
    address: "Adres",
    zipCode: "Postcode",
    city: "Plaats",
    newsletterStatus: "Nieuwsbrief",
    portalStatus: "Portal",
    keycloakId: "Keycloak ID",
  })[key] || labelForField(key);

const buildParentRows = (parentDetails) => {
  const raw = parentDetails?.raw || {};
  const preferred = [
    ["name", "Naam", parentDetails?.name],
    ["email", "E-mail", parentDetails?.email],
    ["phone", "Telefoon", parentDetails?.phone],
    ["address", "Adres", raw.address],
    ["zipCode", "Postcode", raw.zipCode],
    ["city", "Plaats", raw.city],
    ["newsletterStatus", "Nieuwsbrief", raw.newsletterStatus],
    ["portalStatus", "Portal", raw.portalStatus],
  ];
  const rows = preferred
    .filter(([, , value]) => value != null && value !== "")
    .map(([key, label, value]) => ({ key, label, value: toDisplayValue(maskSensitiveValue(key, value)) }));

  Object.entries(raw)
    .filter(([key, value]) => {
      if (value == null || value === "") return false;
      if (preferred.some(([preferredKey]) => preferredKey === key)) return false;
      if (["families", "children"].includes(key)) return false;
      return ["string", "number", "boolean"].includes(typeof value);
    })
    .forEach(([key, value]) => rows.push({ key, label: parentFieldLabel(key), value: toDisplayValue(maskSensitiveValue(key, value)) }));

  return rows;
};

const CopyButton = ({ value, field, copiedField, onCopy }) => {
  if (!value || value === "—") return null;
  const copied = copiedField === field;
  return (
    <button
      type="button"
      onClick={() => onCopy(field, value)}
      className="inline-flex h-8 shrink-0 items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 text-xs font-black text-slate-600 transition hover:border-indigo-200 hover:text-indigo-700 dark:border-gray-700 dark:bg-gray-950 dark:text-slate-300 dark:hover:text-indigo-200"
      title={copied ? "Gekopieerd" : "Kopieer"}
    >
      <FaRegCopy className="h-3 w-3" />
      {copied ? "Gekopieerd" : "Kopieer"}
    </button>
  );
};

interface InfoLineProps {
  icon?: React.ComponentType<{ className?: string }>;
  label?: React.ReactNode;
  value?: React.ReactNode;
  action?: React.ReactNode;
  valueClassName?: string;
}

const InfoLine = ({ icon: Icon, label, value, action, valueClassName = "" }: InfoLineProps) => (
  <div className="flex min-w-0 items-start gap-2.5">
    {Icon && <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />}
    <span className="min-w-0 flex-1">
      {label && <span className="block text-xs font-bold text-slate-500 dark:text-slate-400">{label}</span>}
      <span className={`block break-words text-sm font-semibold text-slate-950 dark:text-slate-100 ${valueClassName}`}>{value || "—"}</span>
    </span>
    {action}
  </div>
);

const ParentInfoPanel = ({ parentDetails, onClose, onOpenFullCard }) => {
  const rows = buildParentRows(parentDetails);
  return (
    <section className="mt-4 rounded-3xl bg-white p-5 shadow-sm ring-1 ring-indigo-200/80 dark:bg-gray-950 dark:ring-indigo-900/60">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-black uppercase tracking-wide text-slate-700 dark:text-slate-300">Ouderinformatie</h3>
          <p className="mt-1 text-sm font-semibold text-slate-500 dark:text-slate-400">{parentDetails?.name || "Ouder"}</p>
        </div>
        <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-gray-900">
          <FaTimes className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="mt-4 divide-y divide-slate-100 overflow-hidden rounded-2xl bg-slate-50 ring-1 ring-slate-200 dark:divide-gray-800 dark:bg-gray-900/60 dark:ring-gray-800">
        {rows.length ? (
          rows.map((row) => (
            <div key={`${row.key}-${row.label}`} className="grid gap-1 px-4 py-3">
              <span className="text-xs font-black uppercase tracking-wide text-slate-500">{row.label}</span>
              <strong className="break-words text-sm font-semibold text-slate-950 dark:text-slate-100">{row.value}</strong>
            </div>
          ))
        ) : (
          <div className="px-4 py-3 text-sm font-semibold text-slate-500 dark:text-slate-400">Geen aanvullende oudergegevens beschikbaar.</div>
        )}
      </div>

      {parentDetails?.id && (
        <button
          type="button"
          onClick={onOpenFullCard}
          className="mt-4 inline-flex min-h-[2.65rem] w-full items-center justify-center rounded-xl bg-indigo-600 px-4 text-sm font-black text-white transition hover:bg-indigo-700"
        >
          Open volledige ouderkaart
        </button>
      )}
    </section>
  );
};

const StatusNote = ({ status }) => {
  if (status !== "PENDING") return null;
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/30 dark:text-amber-100">
      <div className="flex items-center gap-2 text-sm font-black">
        <FaHourglassHalf /> In behandeling
      </div>
      <p className="mt-2 text-sm font-medium leading-6">Dit verzoek wacht op verwerking door de administratie.</p>
    </div>
  );
};

const statusMeta = (status, t) => {
  if (status === "APPROVED") return { label: t("mutations.workspace.status.processed", { defaultValue: "Processed" }), finalLabel: t("mutations.workspace.timeline.processed", { defaultValue: "Processed" }), description: t("mutations.workspace.status.processedDescription", { defaultValue: "This request has been processed by administration." }), tone: "border-green-300 bg-green-50 text-green-900 dark:border-green-700 dark:bg-green-950/30 dark:text-green-100", dot: "bg-green-600 ring-green-600" };
  if (status === "REJECTED") return { label: t("mutations.workspace.status.rejected", { defaultValue: "Not processed" }), finalLabel: t("mutations.workspace.timeline.rejected", { defaultValue: "Rejected" }), description: t("mutations.workspace.status.rejectedDescription", { defaultValue: "This request was rejected by administration." }), tone: "border-red-300 bg-red-50 text-red-900 dark:border-red-700 dark:bg-red-950/30 dark:text-red-100", dot: "bg-red-600 ring-red-600" };
  return { label: t("mutations.workspace.status.inProgress", { defaultValue: "In progress" }), finalLabel: t("mutations.workspace.timeline.processed", { defaultValue: "Processed" }), description: t("mutations.workspace.status.inProgressDescription", { defaultValue: "This request is awaiting processing by administration." }), tone: "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-100", dot: "bg-white ring-slate-400 dark:bg-gray-950" };
};

const TechnicalDialog = ({ type, req, view, details, onClose }) => {
  if (!type) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 px-4 py-6" role="dialog" aria-modal="true">
      <div className="w-full max-w-2xl rounded-2xl border border-indigo-100/70 bg-white p-4 shadow-2xl dark:border-indigo-500/30 dark:bg-gray-950">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">{type === "payload" ? "Payload" : "Technische details"}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Mutatie #{req.id}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-900 dark:hover:text-white">
            <span className="sr-only">Sluiten</span>
            <FaTimes />
          </button>
        </div>
        <pre className="max-h-[65vh] overflow-auto rounded-xl bg-gray-950 p-3 font-mono text-xs text-gray-100">
          {type === "payload" ? formatRawPayload(view?.rawPayload ?? req.payload) : JSON.stringify(maskSensitiveData(details), null, 2)}
        </pre>
      </div>
    </div>
  );
};

interface MutationTableProps {
  requests: DecoratedMutationRequest[];
  onApprove?: (id: EntityId) => void | Promise<void>;
  onReject?: (id: EntityId) => void | Promise<void>;
  onDelete?: (id: EntityId) => void | Promise<void>;
  onRestore?: (id: EntityId) => void | Promise<void>;
  showActions?: boolean;
  showStatus?: boolean;
  variant?: "pending" | "history";
  processingRequestId?: EntityId | null;
  userLookup?: LookupMap<UserSummary>;
  parentLookup?: LookupMap<ParentSummary>;
  childLookup?: LookupMap<ChildSummary>;
  familyLookup?: LookupMap<FamilySummary>;
  initialRequestId?: string | null;
}

export const MutationDrawer = ({ item, onClose, onApprove, onReject, onDelete, onRestore, showActions, showStatus, processingRequestId, t, backButtonRef }) => {
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [showParentInfo, setShowParentInfo] = useState(false);

  if (!item) return null;

  const { req, view, rows, context, lookups } = item;
  const title = getMutationTitle(req, view);
  const isUpdate = String(req.action || view?.action || "").toUpperCase() === "UPDATE";
  const isCreate = String(req.action || view?.action || "").toUpperCase() === "CREATE";
  const familySituation = isFamilySituationRequest(req, view, rows);
  const situationRow = getFamilySituationChange(rows);
  const currentSituation = situationRow?.oldValue && situationRow.oldValue !== "—" ? situationRow.oldValue : "Samenwonend";
  const desiredSituation = situationRow?.newValue || situationRow?.value || "—";
  const explanation = getExplanation(rows);
  const contactRequested = getContactRequested(rows);
  const visibleRows = rows.filter((row) => !["explanation", "note", "wantsContact", "contactRequested", "reason", "familySituationChange"].includes(row.key));

  const technicalDetails = {
    id: req.id,
    entityType: req.entityType || view?.entityType,
    action: req.action || view?.action,
    entityId: req.entityId,
    status: req.status,
    requestedBy: context.requestedBy,
    requestedByUserId: context.requestedById,
    createdAt: context.createdAt,
    reviewedBy: req.reviewedBy ? resolveLabel(req.reviewedBy, lookups.userLookup) : null,
    reviewedAt: req.reviewedAt ? formatDateTime(req.reviewedAt) : null,
  };

  const parentDetails = getParentDetails(req, view, lookups, context);

  const copyText = async (field, value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedField(field);
      window.setTimeout(() => {
        setCopiedField((current) => (current === field ? null : current));
      }, 1400);
    } catch {
      // Clipboard can be unavailable in insecure browser contexts.
    }
  };

  const openFullParentCard = () => {
    if (!parentDetails.id) return;
    navigate(`/family/parent/${encodeURIComponent(parentDetails.id)}/details`);
  };

  const handleApprove = async () => {
    try {
      await onApprove(req.id);
      onClose();
    } catch {
      // The parent presents the API error; keep this detail view open for retry.
    }
  };

  const handleReject = async () => {
    try {
      await onReject(req.id);
      onClose();
    } catch {
      // The parent presents the API error; keep this detail view open for retry.
    }
  };

  const actionDisabled = processingRequestId === req.id;
  const status = statusMeta(context.status, t);
  const DetailIcon = getMutationIcon(req, view);

  return (
      <aside className="flex h-[calc(100dvh-7rem)] max-h-[58rem] min-w-0 flex-col overflow-hidden bg-white dark:bg-gray-950" aria-label={title}>
        <header className="flex shrink-0 items-start gap-3 px-5 pb-3 pt-5 sm:px-7 sm:pt-6">
          <button ref={backButtonRef} type="button" aria-label={t("mutations.workspace.back", { defaultValue: "Back to mutation requests" })} onClick={onClose} className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-indigo-700 transition hover:bg-indigo-50 dark:text-indigo-200 dark:hover:bg-indigo-950/50">
            <FaChevronLeft />
          </button>
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-200"><DetailIcon className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-slate-700 dark:text-slate-300">{title}</p>
            <h2 className="mt-1 truncate text-2xl font-black text-slate-950 dark:text-slate-100">{context.familyName}</h2>
          </div>
          <button type="button" aria-label={t("common.close", { defaultValue: "Close" })} onClick={onClose} className="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-gray-900">
            <FaTimes />
          </button>
        </header>

        <div className="flex shrink-0 flex-wrap items-center gap-5 px-7 pb-5">
          <span className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-black uppercase ${statusColors[context.status] || statusColors.PENDING}`}><span className="h-2.5 w-2.5 rounded-full bg-current" />{status.label}</span>
          <span className="hidden h-9 w-px bg-slate-200 sm:block dark:bg-gray-800" />
          <InfoLine icon={FaCalendarAlt} label={t("mutations.workspace.requestedAt", { defaultValue: "Requested at" })} value={context.createdAt} />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-6 sm:px-7">
          <div className={`rounded-xl border p-4 ${status.tone}`}>
            <div className="flex items-center gap-3 text-sm font-black">{context.status === "APPROVED" ? <FaCheck /> : context.status === "REJECTED" ? <FaTimes /> : <FaHourglassHalf />} {status.label}</div>
            <p className="mt-2 pl-7 text-sm font-medium">{status.description}</p>
          </div>

          {showParentInfo && (
            <ParentInfoPanel
              parentDetails={parentDetails}
              onClose={() => setShowParentInfo(false)}
              onOpenFullCard={openFullParentCard}
            />
          )}

          {familySituation ? (
            <>
              <section className="mt-4 rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200/70 dark:bg-gray-950 dark:ring-gray-800">
                <h3 className="text-xs font-black uppercase tracking-wide text-slate-700 dark:text-slate-300">Wat verandert er?</h3>
                <div className="mt-4 grid gap-3">
                  <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-gray-900/60 dark:ring-gray-800">
                    <span className="text-xs font-black uppercase tracking-wide text-slate-500">Huidige situatie</span>
                    <strong className="mt-2 block text-base font-black text-slate-950 dark:text-slate-100">{currentSituation}</strong>
                    <FaUsers className="mt-3 h-7 w-7 text-indigo-300" />
                  </div>
                  <div className="flex justify-center text-slate-400">↓</div>
                  <div className="rounded-2xl bg-green-50 p-4 ring-1 ring-green-200 dark:bg-green-950/30 dark:ring-green-800/60">
                    <span className="text-xs font-black uppercase tracking-wide text-green-700 dark:text-green-300">Gewenste situatie</span>
                    <strong className="mt-2 block text-base font-black text-slate-950 dark:text-slate-100">{desiredSituation}</strong>
                    <FaUser className="mt-3 h-7 w-7 text-green-500" />
                  </div>
                </div>
              </section>

              <section className="mt-4 rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200/70 dark:bg-gray-950 dark:ring-gray-800">
                <h3 className="flex items-center gap-2 text-sm font-black uppercase tracking-wide text-slate-700 dark:text-slate-300"><FaInfoCircle className="text-indigo-500" /> Toelichting</h3>
                <div className="mt-3 rounded-2xl bg-slate-50 p-4 text-sm font-semibold leading-6 text-slate-700 ring-1 ring-slate-200 dark:bg-gray-900/60 dark:text-slate-200 dark:ring-gray-800">
                  {explanation || "Geen toelichting opgegeven."}
                </div>
              </section>

              <section className="mt-4 rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200/70 dark:bg-gray-950 dark:ring-gray-800">
                <h3 className="flex items-center gap-2 text-sm font-black uppercase tracking-wide text-slate-700 dark:text-slate-300"><FaPhone className="text-indigo-500" /> Contact</h3>
                <div className="mt-3 flex items-start gap-3">
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${contactRequested ? "bg-green-500 text-white" : "bg-slate-200 text-slate-500"}`}><FaCheck className="h-3.5 w-3.5" /></span>
                  <span>
                    <strong className="block text-sm font-black text-slate-950 dark:text-slate-100">{contactRequested ? "Ouder wil graag teruggebeld worden." : "Geen terugbelverzoek."}</strong>
                    <small className="mt-1 block text-sm font-medium leading-6 text-slate-500 dark:text-slate-400">
                      {contactRequested ? "De ouder heeft aangegeven dat school contact met hem mag opnemen." : "De ouder heeft geen extra contactverzoek aangegeven."}
                    </small>
                  </span>
                </div>
              </section>
            </>
          ) : (
            <div className="mt-5 grid gap-4 xl:grid-cols-[minmax(0,1.05fr)_minmax(15rem,.95fr)]">
            <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-950">
              <h3 className="text-sm font-black text-slate-900 dark:text-slate-100">{t("mutations.workspace.changes.title", { defaultValue: "What will happen?" })}</h3>
              <p className="mt-1 text-xs text-slate-500">{isCreate ? t("mutations.workspace.changes.createDescription", { defaultValue: "New data that will be added." }) : t("mutations.workspace.changes.updateDescription", { defaultValue: "Data that will be changed." })}</p>
              <div className="mt-4 divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200 dark:divide-gray-800 dark:border-gray-800">
                {(visibleRows.length ? visibleRows : rows).map((row, index) => (
                  <div key={`${row.label}-${index}`} className="grid grid-cols-[minmax(7rem,.8fr)_minmax(0,1.2fr)_auto] items-center gap-2 px-3 py-2.5 text-sm">
                    <span className="font-bold text-slate-700 dark:text-slate-300">{row.label}</span>
                    {isUpdate && row.oldValue != null ? (
                      <strong className="text-green-700 dark:text-green-300">{row.newValue || "—"}</strong>
                    ) : (
                      <strong className="truncate text-slate-700 dark:text-slate-200">{row.value ?? row.newValue ?? "—"}</strong>
                    )}
                    <span className="rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[10px] font-black text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-300">{isCreate ? t("mutations.workspace.changes.new", { defaultValue: "New" }) : t("mutations.workspace.changes.changed", { defaultValue: "Changed" })}</span>
                  </div>
                ))}
              </div>
            </section>
            <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-950">
              <h3 className="text-sm font-black text-slate-900 dark:text-slate-100">{t("mutations.workspace.requester.title", { defaultValue: "Requester" })}</h3>
              <p className="mt-1 text-xs text-slate-500">{t("mutations.workspace.requester.description", { defaultValue: "Contact details of the requester." })}</p>
              <button type="button" onClick={() => setShowParentInfo((value) => !value)} className="mt-4 flex items-center gap-3 text-left">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-indigo-50 text-sm font-black text-indigo-600 dark:bg-indigo-950/50">{context.requestedBy?.split(/\s+/).map((part) => part[0]).slice(0, 2).join("")}</span>
                <strong className="text-sm text-slate-900 dark:text-slate-100">{context.requestedBy || "—"}</strong>
              </button>
              <div className="mt-5 grid gap-4"><InfoLine icon={FaEnvelope} value={context.email || "—"} /><InfoLine icon={FaPhone} value={context.phone || "—"} /></div>
              {(context.email || context.phone) && <button type="button" onClick={() => copyText("contact", [context.email, context.phone].filter(Boolean).join("\n"))} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-indigo-200 px-3 py-2.5 text-xs font-black text-indigo-700 dark:border-indigo-800 dark:text-indigo-200"><FaRegCopy /> {t("mutations.workspace.requester.copyContact", { defaultValue: "Copy contact" })}</button>}
            </section>
            </div>
          )}

          <section className="mt-4 rounded-xl border border-slate-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-950">
            <div className="flex items-start justify-between gap-3"><div><h3 className="text-sm font-black text-slate-900 dark:text-slate-100">{t("mutations.workspace.timeline.title", { defaultValue: "Request timeline" })}</h3><p className="mt-1 text-xs text-slate-500">{t("mutations.workspace.timeline.description", { defaultValue: "Events and status updates." })}</p></div><button type="button" aria-label={t("mutations.workspace.technicalInfo", { defaultValue: "Technical information" })} onClick={() => setDialog("technical")} className="rounded-full p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-gray-900"><FaInfoCircle /></button></div>
            <ol className="mt-4 ml-1 border-l-2 border-slate-200 pl-6 dark:border-gray-800">
              <li className="relative pb-4"><span className="absolute -left-[1.93rem] top-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-indigo-600 ring-1 ring-indigo-600 dark:border-gray-950" /><strong className="block text-sm">{t("mutations.workspace.timeline.submitted", { defaultValue: "Submitted" })}</strong><span className="text-xs text-slate-500">{context.createdAt}</span></li>
              <li className="relative pb-4"><span className={`absolute -left-[1.93rem] top-0.5 h-3.5 w-3.5 rounded-full border-2 border-white ring-1 ${context.status === "PENDING" ? "bg-amber-400 ring-amber-500" : "bg-indigo-600 ring-indigo-600"} dark:border-gray-950`} /><strong className="block text-sm">{t("mutations.workspace.timeline.inProgress", { defaultValue: "In progress" })}</strong><span className="text-xs text-slate-500">{t("mutations.workspace.timeline.awaitingProcessing", { defaultValue: "Awaiting processing by administration." })}</span></li>
              <li className="relative"><span className={`absolute -left-[1.93rem] top-0.5 h-3.5 w-3.5 rounded-full border-2 border-white ring-1 ${status.dot}`} /><strong className="block text-sm">{status.finalLabel}</strong><span className="text-xs text-slate-500">{context.status === "PENDING" ? t("mutations.workspace.timeline.notProcessedYet", { defaultValue: "Not processed yet" }) : formatDateTime(req.reviewedAt)}</span></li>
            </ol>
          </section>
        </div>

        {showActions && (
          <footer className="grid shrink-0 grid-cols-2 gap-3 border-t border-slate-100 bg-white p-4 dark:border-gray-800 dark:bg-gray-950">
            {context.status === "PENDING" && onApprove && (
              <button disabled={actionDisabled} onClick={handleApprove} className="order-2 inline-flex min-h-[2.85rem] items-center justify-center gap-2 rounded-lg bg-green-600 px-4 text-sm font-black text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60">
                <FaCheck className="h-3.5 w-3.5" /> {t("common.approve")}
              </button>
            )}
            {context.status === "PENDING" && onReject && (
              <button disabled={actionDisabled} onClick={handleReject} className="order-1 inline-flex min-h-[2.85rem] items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 text-sm font-black text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-500/40 dark:bg-red-900/30 dark:text-red-200">
                <FaTimes className="h-3.5 w-3.5" /> {t("common.reject")}
              </button>
            )}
            {context.status !== "PENDING" && onRestore && (
              <button disabled={actionDisabled} onClick={() => onRestore(req.id)} className="inline-flex min-h-[2.85rem] items-center justify-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-4 text-sm font-black text-sky-700 transition hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-sky-500/40 dark:bg-sky-900/40 dark:text-sky-200">
                <FaUndo /> {t("common.restore")}
              </button>
            )}
            {context.status !== "PENDING" && onDelete && (
              <button disabled={actionDisabled} onClick={() => onDelete(req.id)} className="inline-flex min-h-[2.85rem] items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 text-sm font-black text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-200">
                <FaTrash /> {t("common.remove")}
              </button>
            )}
          </footer>
        )}

        <TechnicalDialog type={dialog} req={req} view={view} details={technicalDetails} onClose={() => setDialog(null)} />
      </aside>
  );
};

interface MutationRequestRowProps {
  req: DecoratedMutationRequest;
  onOpen: (item: unknown) => void;
  userLookup: LookupMap<UserSummary>;
  parentLookup: LookupMap<ParentSummary>;
  childLookup: LookupMap<ChildSummary>;
  familyLookup: LookupMap<FamilySummary>;
  t: ReturnType<typeof useTranslation>["t"];
  selected?: boolean;
  buttonRef?: React.Ref<HTMLButtonElement>;
}

const MutationRequestRow: React.FC<MutationRequestRowProps> = ({
  req,
  onOpen,
  userLookup,
  parentLookup,
  childLookup,
  familyLookup,
  t,
  selected = false,
  buttonRef,
}) => {
  const [view, setView] = useState(() => buildFallbackView(req, parentLookup, userLookup));
  const [viewLoading, setViewLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setViewLoading(true);
    http
      .get(`/mutation-requests/${req.id}/view`)
      .then(({ data }) => {
        if (alive) setView(data || buildFallbackView(req, parentLookup, userLookup));
      })
      .catch(() => {
        if (alive) setView(buildFallbackView(req, parentLookup, userLookup));
      })
      .finally(() => {
        if (alive) setViewLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [req, parentLookup, userLookup]);

  const lookups = { userLookup, parentLookup, childLookup, familyLookup };
  const context = getRequestContext(req, view, lookups);
  const rows = getChangedMutationFields(req, view, lookups);
  const title = getMutationTitle(req, view);
  const Icon = getMutationIcon(req, view);
  const situationRow = getFamilySituationChange(rows);
  const summary = situationRow?.newValue || rows[0]?.newValue || rows[0]?.value || getSubjectName(req, view, lookups);
  const status = context.status;

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={() => onOpen({ req, view, rows, context, lookups })}
      aria-current={selected ? "true" : undefined}
      aria-pressed={selected}
      className={`group grid w-full grid-cols-[2.75rem,minmax(0,1fr),auto] items-center gap-4 rounded-2xl border bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-lg dark:bg-gray-950 ${selected ? "border-indigo-400 ring-2 ring-indigo-200 dark:border-indigo-500 dark:ring-indigo-900/70" : "border-slate-200/80 dark:border-gray-800"}`}
    >
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-200">
        <Icon className="h-4 w-4" />
      </span>

      <span className="min-w-0">
        <span className="flex flex-wrap items-center gap-2">
          <strong className="truncate text-base font-black text-slate-950 dark:text-slate-100">{title}</strong>
          <span className={`rounded-md px-2 py-0.5 text-[11px] font-black ${statusColors[status] || statusColors.PENDING}`}>{status}</span>
          {viewLoading && <span className="text-xs font-semibold text-slate-400">laden…</span>}
        </span>
        <span className="mt-1 block text-sm font-semibold text-slate-500 dark:text-slate-400">{summary}</span>
        <span className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
          <span>{context.requestedBy}</span>
          {context.email && <span>{context.email}</span>}
          {context.phone && <span>{context.phone}</span>}
          <span>{context.relativeCreatedAt}</span>
        </span>
      </span>

      <FaChevronRight className="h-4 w-4 text-slate-400 transition group-hover:text-indigo-500" />
    </button>
  );
};

const MutationTable = ({
  requests,
  onApprove,
  onReject,
  onDelete,
  onRestore,
  showActions = false,
  showStatus = false,
  userLookup = new Map(),
  parentLookup = new Map(),
  childLookup = new Map(),
  familyLookup = new Map(),
  initialRequestId = null,
  variant = "pending",
  processingRequestId = null,
}: MutationTableProps) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const selectedRowButtonRef = useRef<HTMLButtonElement>(null);
  const detailBackButtonRef = useRef<HTMLButtonElement>(null);
  const deepLinkOpenedRef = useRef(false);

  const selectedItem = useMemo(() => {
    if (!selected) return null;
    const req = requests?.find((item) => item.id === selected.req.id);
    if (!req) return null;
    const view = selected.view;
    const lookups = selected.lookups;
    return {
      ...selected,
      req,
      view,
      rows: getChangedMutationFields(req, view, lookups),
      context: getRequestContext(req, view, lookups),
    };
  }, [requests, selected]);

  useEffect(() => {
    if (selected && !requests.some((request) => request.id === selected.req.id)) {
      setSelected(null);
    }
  }, [requests, selected]);

  useEffect(() => {
    if (!initialRequestId || deepLinkOpenedRef.current || selected) return;
    const req = requests.find((request) => String(request.id) === String(initialRequestId));
    if (!req) return;

    deepLinkOpenedRef.current = true;
    setStatusFilter(String(req.status || "ALL").toUpperCase());
    const lookups = { userLookup, parentLookup, childLookup, familyLookup };
    http
      .get(`/mutation-requests/${req.id}/view`)
      .then(({ data }) => {
        const view = data || buildFallbackView(req, parentLookup, userLookup);
        setSelected({ req, view, lookups });
      })
      .catch(() => {
        setSelected({
          req,
          view: buildFallbackView(req, parentLookup, userLookup),
          lookups,
        });
      });
  }, [
    childLookup,
    familyLookup,
    initialRequestId,
    parentLookup,
    requests,
    selected,
    userLookup,
  ]);

  const filteredRequests = useMemo(() => requests.filter((req) => {
    const matchesStatus = statusFilter === "ALL" || String(req.status).toUpperCase() === statusFilter;
    if (!matchesStatus) return false;
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return [req.action, req.entityType, req.status, req.requestedBy, JSON.stringify(parsePayload(req.payload))]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(needle));
  }), [query, requests, statusFilter]);

  useEffect(() => {
    if (selectedItem) detailBackButtonRef.current?.focus();
  }, [selectedItem]);

  const closeDetails = () => {
    const rowButtonToRestore = selectedRowButtonRef.current;
    setSelected(null);
    window.setTimeout(() => (rowButtonToRestore?.isConnected ? rowButtonToRestore : document.getElementById("mutation-request-search"))?.focus(), 0);
  };

  return (
    <div
      className={selectedItem
        ? "overflow-hidden rounded-[20px] border border-slate-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-950 lg:grid lg:grid-cols-[minmax(0,47fr)_minmax(0,53fr)]"
        : "overflow-hidden rounded-[20px] border border-slate-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-950"}
    >
      <div className={`${selectedItem ? "hidden h-[calc(100dvh-7rem)] max-h-[58rem] min-w-0 overflow-hidden border-r border-slate-200 lg:flex lg:flex-col dark:border-gray-800" : "flex min-w-0 flex-col"}`}>
        <div className="shrink-0 border-b border-slate-200 p-5 dark:border-gray-800 sm:p-7">
          <div className="flex items-center gap-3"><h2 className="text-xl font-black text-slate-950 dark:text-slate-100">{t("mutations.workspace.title", { defaultValue: "Submitted changes" })}</h2><span className="rounded-md bg-indigo-50 px-2.5 py-1 text-sm font-black text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-200">{requests.length}</span></div>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{t("mutations.workspace.description", { defaultValue: "Search and filter all submitted requests." })}</p>
          <label htmlFor="mutation-request-search" className="mt-5 flex min-w-0 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 dark:border-gray-800 dark:bg-gray-950"><FaSearch className="shrink-0 text-slate-400" /><span className="sr-only">{t("mutations.workspace.searchLabel", { defaultValue: "Search mutation requests" })}</span><input id="mutation-request-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("mutations.workspace.searchPlaceholder", { defaultValue: "Search by name or email..." })} className="h-11 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400" /></label>
          <div className="mt-4 flex gap-2 overflow-x-auto pb-1">{[{ key: "ALL", label: t("mutations.workspace.filters.all", { defaultValue: "All" }) }, { key: "PENDING", label: t("mutations.workspace.filters.inProgress", { defaultValue: "In progress" }) }, { key: "APPROVED", label: t("mutations.workspace.filters.processed", { defaultValue: "Processed" }) }, { key: "REJECTED", label: t("mutations.workspace.filters.notProcessed", { defaultValue: "Not processed" }) }].map(({ key, label }) => { const count = key === "ALL" ? requests.length : requests.filter((request) => String(request.status).toUpperCase() === key).length; return <button key={key} type="button" aria-pressed={statusFilter === key} onClick={() => setStatusFilter(key)} className={`inline-flex shrink-0 items-center gap-2 rounded-lg border px-3 py-2 text-xs font-black ${statusFilter === key ? "border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-200" : "border-slate-200 text-slate-600 dark:border-gray-800 dark:text-slate-300"}`}>{label}<span className="rounded-full bg-slate-100 px-1.5 py-0.5 dark:bg-gray-800">{count}</span></button>; })}</div>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-5">
        {filteredRequests.map((req) => (
          <MutationRequestRow
            key={req.id}
            req={req}
            onOpen={setSelected}
            userLookup={userLookup}
            parentLookup={parentLookup}
            childLookup={childLookup}
            familyLookup={familyLookup}
            t={t}
            selected={selectedItem?.req.id === req.id}
            buttonRef={selectedItem?.req.id === req.id ? selectedRowButtonRef : undefined}
          />
        ))}
        {!filteredRequests.length && <p className="py-8 text-center text-sm font-semibold text-slate-500">{t("mutations.workspace.noResults", { defaultValue: "No requests found." })}</p>}
        </div>
        <div className="flex shrink-0 items-center border-t border-slate-200 px-6 py-4 text-xs font-semibold text-slate-500 dark:border-gray-800"><span>{t("mutations.workspace.resultCount", { count: filteredRequests.length, defaultValue: "{{count}} results" })}</span></div>
      </div>

      <MutationDrawer
        item={selectedItem}
        onClose={closeDetails}
        onApprove={onApprove}
        onReject={onReject}
        onDelete={onDelete}
        onRestore={onRestore}
        showActions={showActions}
        showStatus={showStatus}
        processingRequestId={processingRequestId}
        t={t}
        backButtonRef={detailBackButtonRef}
      />
    </div>
  );
};

export default MutationTable;
